import { can, formatCode, normalizeCode, TEST_CODES, testCodeKind, writeOffOtherSiteMessage, writeOffSiteBlocked, writeOffStateProblem } from '@pl/domain';
import type { BoxDetailDto, BoxState, ScanPalletDto, ScanRequest, ScanResponse, SkuDto } from '@pl/domain';
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useCallback, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { canUse } from '../../auth/roles';
import { api } from '../../lib/api';
import { FOUND_WORDS, WRITE_OFF_WORDS } from '../../lib/boxHistory';
import { stateWords } from '../../lib/format';
import { useMe, useSiteMap, useSites, useSkuMap } from '../../lib/queries';
import { uuid } from '../../lib/uuid';
import { ErrorNote, RefetchNote, SiteTag, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { RedScreen } from '../RedScreen';
import { announceRefusal } from '../refusalGuard';
import { type LocalResult, ResultPanel } from '../ResultPanel';
import { feedback } from '../scanner/feedback';
import { ScannerView } from '../scanner/ScannerView';
import { useScanSite } from '../useScanSite';
import s from './Lookup.module.css';

const ACTIONS = {
  found: { title: 'Found / return', permission: 'found', path: 'found' },
  'write-off': { title: 'Write off', permission: 'write_off', path: 'write-off' },
} as const;
type ActionKey = keyof typeof ACTIONS;

const WRITE_OFF_REASONS = Object.entries(WRITE_OFF_WORDS).map(([value, label]) => ({ value, label }));
const FOUND_REASONS = Object.entries(FOUND_WORDS).map(([value, label]) => ({ value, label }));

const RED_TITLE: Record<string, string> = { UNKNOWN_CODE: 'Unknown sticker', INVALID_CODE: 'Unknown sticker', VOIDED: 'Sticker voided' };

interface BoxView {
  code: string;
  state: BoxState;
  siteId: string | null;
  sku: SkuDto | null;
}

/**
 * What is this box? One scan, one answer: the SKU with its colour, where the box stands, and
 * which site holds it. No stock figures and no lists of other boxes — this screen is used mid-count
 * as often as not, and a worker who can read the expected number off their phone is not counting.
 *
 * A look-up is not queued. It changes nothing, and an answer that arrives an hour later is not an
 * answer, so when there is no signal the screen says so instead of pretending.
 */
export function Lookup() {
  const me = useMe();
  const { site } = useScanSite();
  const skuMap = useSkuMap();
  const siteMap = useSiteMap();
  const sitesQuery = useSites();
  const [params] = useSearchParams();

  const [box, setBox] = useState<BoxView | null>(null);
  const [pallet, setPallet] = useState<ScanPalletDto | null>(null);
  const [red, setRed] = useState<{ title: string; message: string; code?: string } | null>(null);
  const [result, setResult] = useState<LocalResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastRaw, setLastRaw] = useState<string | null>(null);

  // The reason picked for the action on screen. A write-off starts with none, so it records what
  // the lead chose; Found starts at "Found here", as Office's does.
  const [picked, setPicked] = useState<{ action: string; reason: string } | null>(null);
  const [note, setNote] = useState('');
  const [foundSiteId, setFoundSiteId] = useState('');
  const reasonId = useId();
  const noteId = useId();
  const siteId = useId();

  // Never call `setRed` directly — go through here. See the matching comment in ShipOrder and
  // Receive: closing a sheet elsewhere in the app shell has to land in the same render as
  // `RedScreen` mounting, or that sheet's focus trap — still alive until its close actually
  // commits — wins a beat it has no business winning.
  const showRed = useCallback((next: { title: string; message: string; code?: string }) => {
    announceRefusal();
    setRed(next);
  }, []);

  const role = me.data?.role;
  const actionKey = (params.get('action') ?? '') as ActionKey;
  const action = ACTIONS[actionKey];
  const showAction = Boolean(action) && Boolean(role) && can(role!, action.permission);
  const reason = picked?.action === actionKey ? picked.reason : actionKey === 'found' ? 'found_here' : '';
  const setReason = (value: string) => setPicked({ action: actionKey, reason: value });

  const viewOf = useCallback(
    (scanned: NonNullable<ScanResponse['box']>): BoxView => ({
      code: scanned.code,
      state: scanned.state,
      siteId: scanned.siteId,
      sku:
        (scanned.skuId ? skuMap.get(scanned.skuId) : undefined) ??
        (scanned.skuId
          ? { id: scanned.skuId, code: '', name: scanned.skuName ?? 'Unknown SKU', colourName: scanned.colourName, colourHex: scanned.colourHex, attributes: {}, tracking: 'box', active: true }
          : null),
    }),
    [skuMap],
  );

  const look = useCallback(
    async (raw: string) => {
      setLastRaw(raw);
      setError(null);
      setResult(null);
      // A test label goes to the server like any look-up, so the look-up everyone does on day one
      // (runbook step 12) proves the whole road, and is answered "Test sticker".
      const test = testCodeKind(raw);
      const code = test ? TEST_CODES[test] : normalizeCode(raw);
      if (!code) {
        feedback('bad');
        setBox(null);
        setPallet(null);
        showRed({ title: 'Unknown sticker', message: "This doesn't look like one of our stickers.", code: raw.slice(0, 24) });
        return;
      }
      if (!site) return;
      const request: ScanRequest = { clientScanId: uuid(), siteId: site.id, mode: 'lookup', code, scannedAt: new Date().toISOString() };
      setBusy(true);
      try {
        const response = await api.post<ScanResponse>('/scans', request);
        if (response.outcome === 'rejected') {
          feedback('bad');
          setBox(null);
          setPallet(null);
          showRed({ title: RED_TITLE[response.reasonCode ?? ''] ?? "Can't read this sticker", message: response.message, code: formatCode(code) });
          return;
        }
        feedback('ok');
        setBox(response.box ? viewOf(response.box) : null);
        setPallet(response.pallet);
        if (test) setResult({ tone: 'ok', title: 'Test sticker', message: response.message });
      } catch (e) {
        feedback('bad');
        // Not queued, and said plainly: a look-up answered tomorrow is not an answer. The previous
        // box goes with it — a card left on screen under an error is read as this box's answer.
        setBox(null);
        setPallet(null);
        setError(e instanceof Error ? e.message : 'Could not look this up');
      } finally {
        setBusy(false);
      }
    },
    [site, viewOf, showRed],
  );

  const runAction = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<BoxDetailDto>(`/boxes/${encodeURIComponent(box!.code)}/${action.path}`, body),
    onSuccess: (detail) => {
      feedback('ok');
      setBox({ code: detail.code, state: detail.state, siteId: detail.siteId, sku: detail.sku });
      setResult({ tone: 'ok', title: action.title === 'Write off' ? 'Written off' : 'Back in stock', message: `${formatCode(detail.code)} is now ${stateWords(detail.state).toLowerCase()}.` });
      setNote('');
    },
  });

  function submitAction(e: FormEvent) {
    e.preventDefault();
    if (!box) return;
    const trimmed = note.trim();
    runAction.mutate(actionKey === 'found' ? { siteId: foundSiteId || site?.id, reason, note: trimmed || null } : { reason, note: trimmed || null });
  }

  const noteRequired = actionKey === 'write-off' && reason === 'other';
  // Why this box can't take the action on screen, said in place of the form.
  const stateProblem =
    actionKey === 'write-off' && box
      ? (writeOffStateProblem(box.state) ??
        (writeOffSiteBlocked(box, me.data?.siteIds ?? null) ? writeOffOtherSiteMessage((box.siteId && siteMap.get(box.siteId)?.name) || 'another site') : null))
      : null;
  // Found puts the box at one of the lead's own sites; the API refuses any other.
  const mySiteIds = me.data?.siteIds ?? null;
  const foundSites = (sitesQuery.data ?? []).filter((option) => mySiteIds === null || mySiteIds.includes(option.id));
  const reasons = actionKey === 'found' ? FOUND_REASONS : WRITE_OFF_REASONS;
  const canOffice = role ? canUse(role, 'office') : false;

  return (
    <>
      <div className={`scan-body ${s.lkBody}`}>
        {action && (
          <div className="sect">
            {action.title}
            {!showAction && <span className="r">Lead only</span>}
          </div>
        )}

        <ScannerView onCode={(raw) => void look(raw)} enabled={red === null} />

        {busy && (
          <div className={s.lkBusy}>
            <Spinner label="Looking up" />
          </div>
        )}

        {error && (
          <div className={s.lkPanel}>
            <ErrorNote error={new Error(error)} />
            <button type="button" className={`bigbtn ghost ${s.lkRetry}`} onClick={() => lastRaw && void look(lastRaw)}>
              Try again
            </button>
          </div>
        )}

        <ResultPanel result={result} />

        {box && (
          <div className={s.lkCard}>
            <div className={s.lkSku}>
              <SkuTag sku={box.sku} withCode />
            </div>
            <div className={s.lkFacts}>
              <span className={s.lkCode}>{formatCode(box.code)}</span>
              <span className={`chip ${STATE_TONE[box.state] ?? ''}`}>{stateWords(box.state)}</span>
              {box.siteId && (
                <span className={s.lkSite}>
                  <SiteTag site={siteMap.get(box.siteId)} />
                  {siteMap.get(box.siteId)?.name ?? box.siteId}
                </span>
              )}
            </div>
            {canOffice && (
              <Link to={`/office/boxes/${encodeURIComponent(box.code)}`} className={s.lkHistory}>
                Full history
                <Icon name="chev-right" size={18} />
              </Link>
            )}
          </div>
        )}

        {pallet && (
          <div className={s.lkCard}>
            <div className={s.lkFacts}>
              <span className={s.lkPalletIcon}>
                <Icon name="pallet-load" size={22} />
              </span>
              <span className={s.lkCode}>{formatCode(pallet.code)}</span>
              {/* No number while a count covers this pallet: the server leaves it out, as it would be the count's. */}
              <span className="chip">{pallet.boxCount === null ? 'Counting · hidden' : pallet.boxCount === 1 ? '1 box on it' : `${pallet.boxCount} boxes on it`}</span>
            </div>
          </div>
        )}

        {showAction && box && stateProblem && (
          <div className={s.lkPanel}>
            <div className="sect">{action.title}</div>
            <p className="sub">{stateProblem}</p>
          </div>
        )}

        {showAction && box && !stateProblem && (
          <form className={s.lkPanel} onSubmit={submitAction}>
            <div className="sect">{action.title}</div>
            {actionKey === 'found' &&
              (sitesQuery.isLoadingError ? (
                // A failed secondary query stays inside its own panel: the look-up above it is fine.
                // A list of sites that loaded and then failed to refresh is still a list to pick from.
                <>
                  <ErrorNote error={sitesQuery.error} />
                  <button type="button" className={`bigbtn ghost ${s.lkRetry}`} onClick={() => void sitesQuery.refetch()}>
                    Try again
                  </button>
                </>
              ) : (
                <>
                  <label className={s.lkLabel} htmlFor={siteId}>
                    Put it at
                  </label>
                  <select id={siteId} className={`inp lg ${s.lkControl}`} value={foundSiteId || (site?.id ?? '')} onChange={(e) => setFoundSiteId(e.target.value)}>
                    {foundSites.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <RefetchNote queries={[sitesQuery]} />
                </>
              ))}

            <label className={s.lkLabel} htmlFor={reasonId}>
              Reason
            </label>
            <select id={reasonId} className={`inp lg ${s.lkControl}`} value={reason} onChange={(e) => setReason(e.target.value)}>
              {!reason && (
                <option value="" disabled>
                  Choose a reason
                </option>
              )}
              {reasons.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <label className={s.lkLabel} htmlFor={noteId}>
              Note{noteRequired ? '' : ' (optional)'}
            </label>
            <textarea id={noteId} className={`inp lg ${s.lkNote}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} />

            <ErrorNote error={runAction.error} />
            <button type="submit" className="bigbtn" disabled={runAction.isPending || !reason || (noteRequired && note.trim().length === 0)}>
              {action.title}
            </button>
          </form>
        )}
      </div>

      {red && <RedScreen title={red.title} message={red.message} code={red.code} kicker="Not found" note="Nothing was changed." onContinue={() => setRed(null)} />}
    </>
  );
}

const STATE_TONE: Partial<Record<BoxState, string>> = {
  in_stock: 'ok',
  picked: 'accent',
  shipped: 'ink',
  in_transit: 'warn',
  written_off: 'bad',
};
